import React, { useState, useEffect } from 'react';

import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { gql } from '@apollo/client';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import Dialog from 'shared/molecules/Dialog';
import Icon from 'shared/atoms/Icon';
import Pill from 'shared/atoms/Pill';
import Snackbar from 'shared/atoms/Snackbar';
import T from 'shared/atoms/Typography';
import Tooltip from 'shared/atoms/Tooltip';

import theme from 'shared/themes/default';

import AllPlaylistsOverviewTable from './AllPlaylistsOverviewTable';
import EpisodesSeenGraph from './EpisodesSeenGraph';
import PlaylistGroupsWatchDataTable from './PlaylistGroupsWatchDataTable';
import PlaylistWatchDataTable from './PlaylistWatchDataTable';
import TableWithSearchAndExport from './TableWithSearchAndExport';

import { useWatchData } from '../Context';
import {
  listAggregatedWatchData,
  listPlaylistWatchData,
} from '../Context/graphql/queries';
import {
  sendPlaylistReminder,
  sendPlaylistUserReminder,
} from '../Context/graphql/mutations';

const Wrapper = styled.div`
  max-width: 100%;
`;

const WrapperPill = styled.div`
  display: flex;
  margin-top: 108px;
  ${theme.breakpoints.mobile} {
    margin-top: 64px;
  }
`;

const StyledIconWrapper = styled.div`
  margin-left: 0.5rem;
  display: flex;
  align-items: center;
`;

const ShowPlaylistData = () => {
  const [formStatus, setFormStatus] = useState({
    showSnackBar: false,
    success: null,
    message: '',
  });
  const [remindAllDisabled, setRemindAllDisabled] = useState(false);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [userReminderCount, setUserReminderCount] = useState(null);
  const { t } = useTranslation();

  const {
    id,
    isBackOffice,
    fetchPlaylistsWatchData,
    allPlaylistsStats,
    loadingPlaylistsWatchData,
    playlistSelected,
    setPlaylistSelected,
    onSelectItem,
    organizationSelectedId,
    setWatchDataError,
    remindAllEventHandler,
    restClient,
    useGraphQLMutation,
    useLazyGraphQLQuery,
    user,
  } = useWatchData();

  const [listAggregatedWatchDataQuery, listAggregatedWatchDataResponse] =
    useLazyGraphQLQuery(listAggregatedWatchData);
  const [listPlaylistWatchDataQuery, listPlaylistWatchDataResponse] =
    useLazyGraphQLQuery(listPlaylistWatchData);
  const [sendPlaylistReminderMutation, sendPlaylistReminderResponse] =
    useGraphQLMutation(sendPlaylistReminder, {
      onCompleted: () => {
        setRemindAllDisabled(true);
        setFormStatus({
          showSnackBar: true,
          success: true,
          message: t('playlist.reminderSentSuccessfully'),
        });
        setReminderDialogOpen(false);
      },
      onError: () => {
        setFormStatus({
          showSnackBar: true,
          success: false,
          message: t('playlist.reminderSendingFailed'),
        });
        setReminderDialogOpen(false);
      },
      refetchQueries: ['listPlaylistWatchData'], // Will refetch active queries only
      update: (cache, { data: { sendPlaylistReminder } }) => {
        cache.writeFragment({
          id: `PlaylistAggregateWatchData:${sendPlaylistReminder.id}`,
          fragment: gql`
            fragment PlaylistReminder on PlaylistAggregateWatchData {
              lastReminderAt
            }
          `,
          data: {
            lastReminderAt: sendPlaylistReminder.lastReminderAt,
          },
        });
      },
    });
  const [sendPlaylistUserReminderMutation] = useGraphQLMutation(
    sendPlaylistUserReminder,
    {
      onCompleted: () => {
        setFormStatus({
          showSnackBar: true,
          success: true,
          message: t('playlist.reminderSentSuccessfully'),
        });
        setReminderDialogOpen(false);
      },
      onError: () => {
        setFormStatus({
          showSnackBar: true,
          success: false,
          message: t('playlist.reminderSendingFailed'),
        });
        setReminderDialogOpen(false);
      },
      update: (cache, { data: { sendPlaylistUserReminder } }) => {
        cache.writeFragment({
          id: `UserPlaylistWatchData:${sendPlaylistUserReminder.userId}#${sendPlaylistUserReminder.playlistId}`,
          fragment: gql`
            fragment PlaylistUserReminder on UserPlaylistWatchData {
              lastReminderAt
            }
          `,
          data: {
            lastReminderAt: sendPlaylistUserReminder.lastReminderAt,
          },
        });
      },
    }
  );

  const aggregatedWatchData =
    listAggregatedWatchDataResponse.data?.listAggregatedWatchData || [];
  const playlistWatchData =
    listPlaylistWatchDataResponse.data?.listPlaylistWatchData || [];

  useEffect(() => {
    const { error } = listAggregatedWatchDataResponse;

    if (error) {
      setWatchDataError(true);
    }
  }, [listAggregatedWatchDataResponse]);

  useEffect(() => {
    const { error } = listPlaylistWatchDataResponse;

    if (error) {
      setWatchDataError(true);
    }
  }, [listPlaylistWatchDataResponse]);

  useEffect(() => {
    const filter = {
      playlistId: 'all',
      organizationId: organizationSelectedId,
    };
    const variables = { organizationId: organizationSelectedId };

    if (playlistSelected) {
      filter.playlistId = playlistSelected.id;
      variables.playlistId = playlistSelected.id;

      if (playlistSelected.lastReminderAt) {
        const now = new Date();
        const lastReminderAt = new Date(playlistSelected.lastReminderAt);
        // Disable button if last reminder was sent less than 48 hours ago
        setRemindAllDisabled(now - lastReminderAt < 48 * 60 * 60 * 1000);
      } else {
        setRemindAllDisabled(false);
      }

      listPlaylistWatchDataQuery({ variables });
    } else {
      fetchPlaylistsWatchData({ variables });
    }

    listAggregatedWatchDataQuery({
      variables: {
        filter,
      },
    });
  }, [organizationSelectedId, playlistSelected]);

  useEffect(() => {
    if (id && !loadingPlaylistsWatchData && allPlaylistsStats) {
      const playlist = allPlaylistsStats.find((item) => item.id === id);
      if (playlist) {
        setPlaylistSelected(playlist);
      } else {
        onSelectItem();
      }
    }
  }, [allPlaylistsStats, id, loadingPlaylistsWatchData]);

  useEffect(() => {
    const userPlaylistWatchData = (playlistWatchData || []).filter(
      ({ __typename }) => __typename === 'UserPlaylistWatchData'
    );

    if (userPlaylistWatchData.length) {
      const now = new Date();
      const count = userPlaylistWatchData.filter(
        (user) =>
          user.progress < 100 &&
          now - new Date(user.lastReminderAt) > 48 * 60 * 60 * 1000
      ).length;
      setUserReminderCount(count);
      if (count === 0) {
        setRemindAllDisabled(true);
      }
    } else {
      setUserReminderCount(null);
    }
  }, [playlistWatchData]);

  const handleReminder = (userId, playlistId, lastReminderAt) => {
    if (userId) {
      const currentDate = new Date().toISOString();
      const milliseconds = Math.abs(
        new Date(currentDate) - new Date(lastReminderAt)
      );
      const hours = milliseconds / 36e5;
      if (hours < 48) {
        setFormStatus({
          showSnackBar: true,
          success: false,
          message: t('playlist.reminderSentAlready'),
        });
      } else {
        sendPlaylistUserReminderMutation({
          variables: {
            input: {
              userId,
              playlistId,
            },
          },
        });
      }
    } else {
      if (remindAllEventHandler) {
        remindAllEventHandler();
      }

      sendPlaylistReminderMutation({
        variables: {
          playlistId,
        },
      });
    }
  };

  const playlistName = playlistSelected?.id
    ? playlistSelected.name
    : t('playlist.allPlaylists');

  const showReminder =
    isBackOffice || playlistSelected?.ownerId === user.attributes.sub;

  return (
    <Wrapper>
      <>
        <WrapperPill>
          <Pill
            color={theme.palette.lightPure}
            fontColor={theme.palette.mainDark}
          >
            {t('playlist.playlists').toUpperCase()}
          </Pill>
          <Tooltip title={t('watchData.titleTooltip')} arrow>
            <StyledIconWrapper>
              <Icon
                color={theme.palette.lightPure}
                colorHover={theme.palette.brand}
                icon={InfoOutlinedIcon}
                size="2rem"
                sx={{
                  cursor: 'pointer',
                }}
              />
            </StyledIconWrapper>
          </Tooltip>
        </WrapperPill>
        <T
          color={theme.palette.brand}
          fontWeight="medium"
          phoneVariant="headingXs"
          sx={{
            marginBottom: theme.spacing.m3,
            marginTop: theme.spacing.m2,
          }}
          variant="headingS"
        >
          {playlistName}
        </T>
        <EpisodesSeenGraph
          data={aggregatedWatchData}
          loading={listAggregatedWatchDataResponse.loading}
        />
        {!playlistSelected && (
          <TableWithSearchAndExport
            itemsToDisplay={allPlaylistsStats || []}
            downloadItems={() =>
              restClient.get(
                `/watchData/organizations/${organizationSelectedId}/playlists`
              )
            }
            tableTitle={t('watchData.activitiesOfYourPlaylists')}
            searchPlaceHolder={t('watchData.searchForPlaylists')}
            TableToDisplay={AllPlaylistsOverviewTable}
            tableProps={{ selectPlaylistHandler: onSelectItem }}
            showRowsAreClickableMessage
            loading={loadingPlaylistsWatchData}
            emptyMessage={t('watchData.noActivity')}
            spinnerDuration={90}
          />
        )}
        {playlistSelected?.id && (
          <>
            {playlistSelected.shareType === 'GROUP' && (
              <TableWithSearchAndExport
                itemsToDisplay={playlistWatchData}
                downloadItems={() =>
                  restClient.get(
                    `/watchData/organizations/${organizationSelectedId}/playlists/${playlistSelected.id}`
                  )
                }
                tableTitle={t('watchData.activitiesOfYourPlaylists')}
                searchPlaceHolder={t('watchData.searchForPlaylists')}
                TableToDisplay={PlaylistGroupsWatchDataTable}
                tableProps={{
                  organizationId: organizationSelectedId,
                  playlistId: playlistSelected.id,
                  handleReminder,
                  showReminder,
                }}
                showRowsAreClickableMessage
                loading={listPlaylistWatchDataResponse.loading}
                emptyMessage={t('watchData.noPlaylistActivity')}
                secondaryButton={{
                  props: {
                    onClick: () => setReminderDialogOpen(true),
                    disabled: remindAllDisabled,
                    loading: sendPlaylistReminderResponse.loading,
                  },
                  text: t('watchData.remindAll'),
                }}
              />
            )}
            {playlistSelected.shareType !== 'GROUP' && (
              <TableWithSearchAndExport
                itemsToDisplay={playlistWatchData}
                downloadItems={() =>
                  restClient.get(
                    `/watchData/organizations/${organizationSelectedId}/playlists/${playlistSelected.id}`
                  )
                }
                tableTitle={t('watchData.activitiesOfYourPlaylists')}
                searchPlaceHolder={t('watchData.searchForPlaylists')}
                TableToDisplay={PlaylistWatchDataTable}
                tableProps={{
                  playlistId: playlistSelected.id,
                  handleReminder,
                  showReminder,
                }}
                showRowsAreClickableMessage
                loading={
                  listPlaylistWatchDataResponse.loading ||
                  sendPlaylistReminderResponse.loading
                }
                emptyMessage={t('watchData.noPlaylistActivity')}
                secondaryButton={{
                  props: {
                    onClick: () => setReminderDialogOpen(true),
                    disabled: remindAllDisabled,
                    loading: sendPlaylistReminderResponse.loading,
                  },
                  text: t('watchData.remindAll'),
                }}
              />
            )}
          </>
        )}
      </>
      <Dialog
        actions={[
          {
            ariaLabel: t('generic.cancel'),
            handler: () => setReminderDialogOpen(false),
            label: t('generic.cancel'),
            type: 'secondaryOutline',
          },
          {
            ariaLabel: t('watchData.sendReminder'),
            handler: () => handleReminder(null, playlistSelected.id),
            label: t('watchData.sendReminder'),
            loading: sendPlaylistReminderResponse.loading,
          },
        ]}
        ariaLabel={t('watchData.remindAll')}
        onClose={() => setReminderDialogOpen(false)}
        open={reminderDialogOpen}
        title={t('watchData.sendReminder')}
      >
        <T color={theme.palette.pureDark}>
          {userReminderCount === null
            ? t('watchData.remindAllNoCountText')
            : t('watchData.remindAllText', { count: userReminderCount })}
        </T>
      </Dialog>
      <Snackbar
        type={formStatus.success ? 'success' : 'error'}
        message={formStatus.message}
        open={formStatus.showSnackBar}
        onClose={() => {
          setFormStatus({
            showSnackBar: false,
            success: null,
            message: '',
          });
        }}
      />
    </Wrapper>
  );
};

export default ShowPlaylistData;
